import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import { v4 as uuid4 } from 'uuid';
import TextBlock from '@component/common/block/text-block';
import BoxBlock from '@component/common/block/box-block';
import ImageBlock from '@component/common/block/image-block';
import IconTextBlock from '@component/common/block/icon-text-block';
import Gap from '@component/common/gap';
import * as size from '@constant/size';
import { useAuthPage } from '@hook/auth/useAuth';
import content from './content.json';
import tabletImage from '@asset/image/gridgio/landing/tablet.png';
import captureImage from '@asset/image/gridgio/landing/capture.png';
import cloudImage from '@asset/image/gridgio/landing/cloud.png';
import reportImage from '@asset/image/gridgio/landing/report.png';

const LANDING_COLOR = {
  PAPER: '#eeeee6',
  INK: '#233d32',
  MUTED: '#68756b',
  HEADER_BACKGROUND: '#eeeee6ed',
  HEADER_BORDER: '#233d3218',
  BORDER: '#9ba891',
  SAGE: '#dce2d2',
  IMAGE_LABEL_BACKGROUND: '#233d32d9',
  RECORD_BORDER: '#acb6a4',
  PLAN_BORDER: '#a8b59d',
  ILLUSTRATION_BACKGROUND: '#e2e6da',
  DRAWING_LINE: '#80917a',
  DATA_LINE: '#9cb184',
  FOOTER_BORDER: '#4d6049',
  NUMBER: '#809074',
  HERO_ACCENT: '#7a8975',
  DARK_DESCRIPTION: '#bfccbf',
  CLOUD_ACCENT: '#dce8c8',
  PLAN_BACKGROUND: '#e2e6d8',
  PLAN_ACCENT: '#7a8870',
  FOOTER_BACKGROUND: '#182e26',
  FOOTER_LABEL: '#b5c6a7',
  FOOTER_DESCRIPTION: '#b6c2b3',
  FOOTER_NOTE: '#91a08b',
  STATUS_DOT: '#5f7b4a',
  SHEET: '#fafbf4',
  DATABASE: '#294637',
  DRAWING_MARK: '#aa674d',
  PROGRESS: '#b3cd84',
  TAG_BORDER: '#c4ccbc',
  FREE_LABEL: '#f5f6ed',
  CLOUD_LABEL_BORDER: '#80907b',
  DRAFT_LABEL_BACKGROUND: '#e8dfc5',
  DRAFT_LABEL_TEXT: '#705f37',
  WORKFLOW_BACKGROUND: '#e0e5d8',
  WORKFLOW_ACTIVE: '#294334',
  WORKFLOW_BORDER: '#a8b69a',
  CAD_BACKGROUND: '#243039',
  CAD_LINE: '#87918b',
  CAD_LABEL: '#abb9ad',
  CRACK: '#eeae87',
  LEAK: '#87b8df',
  SPALL: '#bacd92',
  DAMAGE_LABEL: '#e5eadd',
  DATABASE_BORDER: '#d4dec9',
  DATABASE_HEADER: '#849178',
  AI_BACKGROUND: '#263f33',
  AI_DESCRIPTION: '#c0cbbb',
  DRAFT_BORDER: '#d5ddce',
  DRAFT_HEADING_BORDER: '#59734a',
  DRAFT_LINE: '#dde3d4',
  DETECTION_BORDER: '#a5b39b',
  STEP_NUMBER: '#7c8c6d',
} as const;

const PRODUCTS = [
  { id: 'inspect', label: 'GridGIO-Inspect' },
  { id: 'ai', label: 'GridGIO-AI' },
] as const;
const LANGUAGES = [
  { id: 'ko', label: '한국어' },
  { id: 'ja', label: '日本語' },
  { id: 'en', label: 'English' },
] as const;

const Wrapper = styled.main`
  width: 100%;
  flex-shrink: 0;
  min-height: 100%;
  background: ${LANDING_COLOR.PAPER};
  color: ${LANDING_COLOR.INK};
  .landing-motion & .landing-reveal {
    opacity: 0;
    transform: translateY(30px);
    transition:
      opacity 0.8s ease,
      transform 0.8s ease;
  }
  .landing-motion & .landing-reveal.is-visible {
    opacity: 1;
    transform: none;
  }
  @media (prefers-reduced-motion: reduce) {
    .landing-motion & .landing-reveal {
      opacity: 1;
      transform: none;
      transition: none;
    }
  }
`;
const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  min-height: 86px;
  padding: 12px 5%;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: ${LANDING_COLOR.HEADER_BACKGROUND};
  backdrop-filter: blur(18px);
  border-bottom: 1px solid ${LANDING_COLOR.HEADER_BORDER};
  @media (max-width: 650px) {
    flex-wrap: wrap;
    padding: 12px 6%;
  }
`;
const BrandContainer = styled.div`
  display: flex;
  align-items: center;
`;
const HeaderControlContainer = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
  @media (max-width: 650px) {
    gap: 10px;
  }
`;
const SelectContainer = styled.details`
  position: relative;
`;
const SelectTriggerContainer = styled.summary`
  padding: 10px 4px;
  list-style: none;
  cursor: pointer;

  &::-webkit-details-marker {
    display: none;
  }
`;
const SelectItemsContainer = styled.div<{ $minWidth?: string }>`
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 1;
  min-width: ${(props) => props.$minWidth ?? '180px'};
  padding: 5px;
  background: ${LANDING_COLOR.PAPER};
  border: 1px solid ${LANDING_COLOR.BORDER};
  border-radius: 8px;
`;
const SelectItemContainer = styled.div`
  padding: 7px 10px;
  border-radius: 4px;

  &:hover {
    background: ${LANDING_COLOR.SAGE};
  }
`;
const ContentContainer = styled.div`
  width: min(1280px, 84%);
  margin: 0 auto;
  @media (max-width: 900px) {
    width: 88%;
  }
`;
const HeroContainer = styled.section`
  padding-top: 70px;
  @media (max-width: 650px) {
    padding-top: 45px;
  }
`;
const EyebrowContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 26px;
  letter-spacing: 1.9px;
  && div {
    font-size: 10px;
    letter-spacing: inherit;
  }
  @media (max-width: 650px) {
    letter-spacing: 1.4px;
    && div {
      font-size: 8px;
    }
  }
`;
const StatusDot = styled.span`
  width: 7px;
  height: 7px;
  flex-shrink: 0;
  margin-right: 10px;
  border-radius: 50%;
  background: ${LANDING_COLOR.STATUS_DOT};
`;
const HeroHeadingContainer = styled.div`
  font-size: clamp(36px, 5vw, 78px);
  font-family: Arial, 'Apple SD Gothic Neo', 'Malgun Gothic', sans-serif;
  font-weight: 400;
  line-height: 1.2;
  letter-spacing: -0.055em;
  word-break: keep-all;

  && div {
    font: inherit;
    letter-spacing: inherit;
  }

  @media (max-width: 650px) {
    font-size: clamp(28px, 7.2vw, 42px);
  }
`;
const HeadingContainer = styled.div.attrs({ className: 'landing-reveal' })`
  font-family: Arial, 'Apple SD Gothic Neo', 'Malgun Gothic', sans-serif;
  font-size: clamp(32px, 3.6vw, 56px);
  font-weight: 400;
  line-height: 1.2;
  letter-spacing: -0.055em;
  word-break: keep-all;

  && div {
    font: inherit;
    letter-spacing: inherit;
    white-space: pre-line;
  }
`;
const DescriptionContainer = styled.div`
  font-family: Arial, 'Apple SD Gothic Neo', 'Malgun Gothic', sans-serif;
  font-size: 15px;
  line-height: 1.9;
  && div {
    font: inherit;
  }
  @media (max-width: 650px) {
    font-size: 13px;
  }
  white-space: pre-line;
  word-break: keep-all;
`;
const HeroBottomContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 30px;
  @media (max-width: 650px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;
const ImageContainer = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 4px;
`;
const ImageLabelContainer = styled.div`
  position: absolute;
  bottom: 20px;
  left: 24px;
  padding: 10px 13px;
  background: ${LANDING_COLOR.IMAGE_LABEL_BACKGROUND};
  letter-spacing: 1.3px;
`;
const MetaContainer = styled.div`
  && div {
    font-size: 8px;
    letter-spacing: inherit;
  }
  display: flex;
  justify-content: space-between;
  letter-spacing: 1.7px;
`;
const Section = styled.section<{ $background?: string }>`
  padding: 120px 0;
  background: ${(props) => props.$background ?? 'transparent'};
  scroll-margin-top: 120px;
  @media (max-width: 900px) {
    padding: 85px 0;
  }
  @media (max-width: 650px) {
    padding: 70px 0;
  }
`;
const RecordSection = styled(Section)`
  padding-top: 150px;
  @media (max-width: 650px) {
    padding-top: 90px;
  }
`;
const CaptureSection = styled.section`
  position: relative;
  height: 175vh;
  background: ${LANDING_COLOR.SAGE};
  scroll-margin-top: 100px;
  @media (max-width: 650px), (prefers-reduced-motion: reduce) {
    height: auto;
  }
`;
const CaptureStageContainer = styled.div`
  position: sticky;
  top: 86px;
  min-height: calc(100svh - 86px);
  padding: 42px 0;
  ${HeadingContainer} {
    font-size: clamp(32px, 3.1vw, 48px);
  }
  ${DescriptionContainer} {
    font-size: 13px;
  }
  @media (max-width: 650px), (prefers-reduced-motion: reduce) {
    position: relative;
    top: 0;
    min-height: 0;
    padding: 65px 0;
  }
`;
const CaptureCopyContainer = styled.div.attrs({ className: 'landing-reveal' })`
  position: relative;
`;
const CaptureDescriptionContainer = styled(DescriptionContainer)`
  position: absolute;
  top: 40px;
  left: 57%;
  max-width: 43%;
  @media (max-width: 900px) {
    left: 55%;
    max-width: 45%;
  }
  @media (max-width: 650px) {
    position: static;
    max-width: 100%;
    margin-top: 23px;
    font-size: 12px;
  }
`;
const CaptureStepItemContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
`;
const CaptureStepNumberContainer = styled.div`
  font:
    italic 17px Georgia,
    serif;
  && div {
    font: inherit;
  }
`;
const CaptureImageContainer = styled.div`
  width: min(1000px, 84%);
  margin: 30px auto 0;
  @media (min-width: 1500px) {
    width: min(1100px, 84%);
  }
  @media (min-width: 651px) {
    img {
      max-height: 52vh;
      width: 100%;
      object-fit: contain;
    }
  }
  @media (max-width: 650px) {
    width: 94%;
  }
`;
const CloudSection = styled(Section)`
  ${HeadingContainer} {
    font-size: clamp(30px, 3.2vw, 50px);
  }
  ${DescriptionContainer} {
    font-size: 13px;
  }
`;
const CloudImageContainer = styled.div.attrs({ className: 'landing-reveal' })`
  position: relative;
`;
const CloudWordContainer = styled.div`
  position: absolute;
  bottom: -35px;
  right: 3%;
  font:
    italic clamp(40px, 8vw, 130px) Georgia,
    serif;
  pointer-events: none;
  && div {
    font: inherit;
  }
  @media (max-width: 650px) {
    bottom: -18px;
    font-size: 45px;
  }
`;
const PlansSection = styled(Section)`
  ${HeadingContainer} {
    font-size: clamp(28px, 3vw, 46px);
  }
  ${DescriptionContainer} {
    font-size: 13px;
  }
`;
const ContactSection = styled(Section)`
  padding-bottom: 30px;
  ${HeadingContainer} {
    font-size: clamp(36px, 5.8vw, 85px);
  }
  ${DescriptionContainer} {
    font-size: 14px;
  }
  @media (max-width: 650px) {
    ${HeadingContainer} {
      font-size: 40px;
    }
  }
`;
const RecordItemsContainer = styled.div.attrs({ className: 'landing-reveal' })`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 40px;
  @media (max-width: 650px) {
    grid-template-columns: 1fr;
    gap: 22px;
  }
`;
const RecordItemContainer = styled.article`
  ${DescriptionContainer} {
    font-size: 13px;
  }
  border-top: 1px solid ${LANDING_COLOR.RECORD_BORDER};
  padding-top: 23px;
`;
const NumberContainer = styled.div`
  font:
    italic 35px Georgia,
    serif;

  div {
    font: inherit;
  }
`;
const TagItemsContainer = styled.div.attrs({ className: 'landing-reveal' })`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;
const DamageItemContainer = styled.div<{ $highlight: boolean }>`
  padding: 10px 15px;
  border: 1px solid ${(props) => (props.$highlight ? LANDING_COLOR.INK : LANDING_COLOR.TAG_BORDER)};
  border-radius: 25px;
  background: ${(props) => (props.$highlight ? LANDING_COLOR.INK : 'transparent')};
  @media (max-width: 650px) {
    padding: 8px 12px;
  }
`;
const SplitContainer = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 50px;
  align-items: end;
  @media (max-width: 650px) {
    grid-template-columns: 1fr;
    gap: 28px;
  }
`;
const StepItemsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 28px;
`;
const PlanItemsContainer = styled.div.attrs({ className: 'landing-reveal' })`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 40px;
  @media (max-width: 650px) {
    grid-template-columns: 1fr;
  }
`;
const PlanItemContainer = styled.article`
  position: relative;
  min-height: 190px;
  @media (max-width: 650px) {
    min-height: 0;
  }
  padding-top: 28px;
  border-top: 1px solid ${LANDING_COLOR.PLAN_BORDER};
`;
const PlanMarkContainer = styled.div`
  position: absolute;
  top: 30px;
  right: 0;
`;
const MigrationVisualContainer = styled.div`
  position: relative;
  min-height: 400px;
  background: ${LANDING_COLOR.ILLUSTRATION_BACKGROUND};
  border-radius: 50% 50% 3px 3px;
  @media (max-width: 900px) {
    min-height: 330px;
  }
  @media (max-width: 650px) {
    min-height: 320px;
    width: 85%;
    margin: 20px auto;
  }
`;
const MigrationLabelContainer = styled.div`
  position: absolute;
  bottom: -30px;
  left: 0;
  letter-spacing: 2px;
`;
const DrawingSheetContainer = styled.div`
  position: absolute;
  top: 15%;
  left: 0;
  width: 56%;
  min-height: 64%;
  padding: 22px;
  background: ${LANDING_COLOR.SHEET};
  box-shadow: 0 15px 35px ${LANDING_COLOR.HEADER_BORDER};
  transform: rotate(-9deg);
`;
const DatabaseSheetContainer = styled(DrawingSheetContainer)`
  top: 30%;
  left: auto;
  right: -2%;
  background: ${LANDING_COLOR.DATABASE};
  transform: rotate(7deg);
`;
const ConvertArrowContainer = styled.div`
  position: absolute;
  top: 14%;
  right: 16%;
`;
const DraftContainer = styled.div`
  padding: 35px;
  background: ${LANDING_COLOR.SHEET};
  border: 1px solid ${LANDING_COLOR.DRAFT_BORDER};
  box-shadow: 0 25px 60px ${LANDING_COLOR.HEADER_BORDER};
  transform: rotate(2deg);
  @media (max-width: 650px) {
    padding: 25px;
  }
`;
const DrawingGrid = styled.div`
  position: relative;
  width: 100%;
  height: 110px;
  margin-top: 16px;
  border: 1px solid ${LANDING_COLOR.DRAWING_LINE};
  background:
    repeating-linear-gradient(90deg, transparent 0, transparent 29px, ${LANDING_COLOR.DRAWING_LINE} 30px),
    repeating-linear-gradient(0deg, transparent 0, transparent 24px, ${LANDING_COLOR.DRAWING_LINE} 25px);
  &::after {
    content: '';
    position: absolute;
    top: 35%;
    left: 25%;
    width: 45%;
    height: 1px;
    background: ${LANDING_COLOR.DRAWING_MARK};
    transform: rotate(-32deg);
  }
`;
const DataLine = styled.div`
  width: 100%;
  margin-top: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${LANDING_COLOR.DATA_LINE};
`;
const WorkflowContainer = styled.div.attrs({ className: 'landing-reveal' })`
  ${DescriptionContainer} {
    font-size: 12px;
  }
  margin-top: 55px;
  padding: 30px;
  border-radius: 4px;
  background: ${LANDING_COLOR.WORKFLOW_BACKGROUND};
  @media (max-width: 650px) {
    padding: 20px 15px;
  }
`;
const WorkflowControlContainer = styled.div`
  display: flex;
  gap: 10px;
  margin: 25px 0;
  flex-wrap: wrap;
`;
const WorkflowGridContainer = styled.div`
  display: flex;
  align-items: stretch;
  gap: 20px;
  @media (max-width: 900px) {
    flex-direction: column;
  }
`;
const CadContainer = styled.div`
  min-width: 0;
  flex: 1;
`;
const CadVisualContainer = styled.div`
  position: relative;
  width: 100%;
  svg {
    display: block;
    width: 100%;
    border-radius: 3px;
  }
`;
const CadLabelContainer = styled.div<{ $x: number; $y: number }>`
  position: absolute;
  left: ${(props) => (props.$x / 620) * 100}%;
  top: ${(props) => ((props.$y - 9) / 300) * 100}%;
  pointer-events: none;
`;
const DatabaseContainer = styled.aside`
  min-width: 270px;
  padding: 20px;
  background: ${LANDING_COLOR.SHEET};
  @media (max-width: 900px) {
    min-width: 0;
  }
`;
const DatabaseRowContainer = styled.div`
  display: grid;
  grid-template-columns: 50px minmax(0, 1fr) 55px;
  gap: 8px;
  padding: 14px 0;
  border-top: 1px solid ${LANDING_COLOR.DATABASE_BORDER};
  align-items: center;
  @media (max-width: 900px) {
    grid-template-columns: 70px minmax(0, 1fr) 80px;
  }
`;
const AiSplitContainer = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 80px;
  align-items: center;
  @media (max-width: 650px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;
const DraftHeadingContainer = styled.div`
  padding-bottom: 20px;
  border-bottom: 2px solid ${LANDING_COLOR.DRAFT_HEADING_BORDER};
`;
const DraftFieldContainer = styled.div`
  padding-top: 18px;
`;
const DraftLine = styled.div<{ $short?: boolean }>`
  width: ${(props) => (props.$short ? '72%' : '100%')};
  height: 5px;
  margin-bottom: 7px;
  background: ${LANDING_COLOR.DRAFT_LINE};
`;
const DetectionContainer = styled.div.attrs({ className: 'landing-reveal' })`
  margin-top: 75px;
  padding: 30px 0;
  border-top: 1px solid ${LANDING_COLOR.DETECTION_BORDER};
`;
const BackLinkContainer = styled.div`
  width: fit-content;
  padding: 12px 0;
  border-bottom: 1px solid ${LANDING_COLOR.INK};
`;
const FooterContainer = styled.footer`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-top: 100px;
  border-top: 1px solid ${LANDING_COLOR.FOOTER_BORDER};
  padding-top: 25px;
`;
const DisclaimerContainer = styled.div`
  line-height: 1.6;
  @media (max-width: 650px) {
    && div {
      font-size: 8px;
    }
  }
`;
const ProgressContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  z-index: 20;
  width: 100%;
  height: 3px;
  background: ${LANDING_COLOR.PROGRESS};
  transform: scaleX(0);
  transform-origin: left;
  pointer-events: none;
`;
const createTextDesign = (color: string = LANDING_COLOR.INK) => ({
  box: { width: '100%', alignX: 'left' as const },
  text: { size: 'medium' as const, color },
});
const createLandingButtonDesign = (filled = true) => ({
  box: {
    width: 'fit-content',
    padding: '18px 25px',
    borderRadius: '35px',
    border: true,
    borderWidth: '1px',
    borderColor: LANDING_COLOR.INK,
    backgroundColor: filled ? LANDING_COLOR.INK : 'transparent',
    gap: '38px',
    event: { cursor: 'pointer' as const },
  },
  text: { custom: { fontSize: 12 }, color: filled ? LANDING_COLOR.PAPER : LANDING_COLOR.INK },
  icon: { size: 'small' as const, color: filled ? LANDING_COLOR.PAPER : LANDING_COLOR.INK },
});
const IMAGE_DESIGN = { box: { width: '100%' }, image: { width: '100%', height: 'auto' } } as const;

interface IProps {
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
}

const Landing: React.FC<IProps> = ({ scrollContainerRef }) => {
  const heroImageRef = useRef<HTMLDivElement>(null);
  const captureSectionRef = useRef<HTMLElement>(null);
  const captureImageRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const { openAuthPage } = useAuthPage();
  const productSelectRef = useRef<HTMLDetailsElement>(null);
  const languageSelectRef = useRef<HTMLDetailsElement>(null);
  const [product, setProduct] = useState<'inspect' | 'ai'>('inspect');
  const [language, setLanguage] = useState<'ko' | 'ja' | 'en'>('ko');
  const [workflowStep, setWorkflowStep] = useState(0);
  const featureRef = useRef<HTMLElement>(null);
  const contactRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const root = scrollContainerRef.current;
    if (!root) return;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let frame = 0;
    const clamp = (value: number) => Math.max(0, Math.min(1, value));
    const paint = () => {
      const height = root.clientHeight;
      const total = root.scrollHeight - height;
      if (progressRef.current)
        progressRef.current.style.transform = `scaleX(${total > 0 ? clamp(root.scrollTop / total) : 0})`;
      const hero = heroImageRef.current;
      const picture = captureImageRef.current;
      if (reducedMotion.matches) {
        if (hero) hero.style.transform = '';
        if (picture) picture.style.transform = '';
        return;
      }
      const rootTop = root.getBoundingClientRect().top;
      if (hero) {
        const rect = hero.getBoundingClientRect();
        const progress = clamp((height - (rect.top - rootTop)) / (height + rect.height));
        hero.style.transform = `scale(${0.97 + 0.03 * progress})`;
      }
      if (picture && captureSectionRef.current) {
        const section = captureSectionRef.current;
        const progress = clamp(
          -(section.getBoundingClientRect().top - rootTop) / Math.max(1, section.offsetHeight - height),
        );
        picture.style.transform =
          window.innerWidth > 650 ? `translateY(${-progress * 22}px) scale(${0.96 + progress * 0.04})` : '';
      }
    };
    const schedulePaint = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(paint);
    };
    let observer: IntersectionObserver | undefined;
    const previousScrollBehavior = root.style.scrollBehavior;
    const setupMotion = () => {
      observer?.disconnect();
      root.classList.remove('landing-motion');
      root.style.scrollBehavior = reducedMotion.matches ? 'auto' : 'smooth';
      const reveals = root.querySelectorAll<HTMLElement>('.landing-reveal');
      if (!reducedMotion.matches) {
        root.classList.add('landing-motion');
        observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-visible');
              observer?.unobserve(entry.target);
            });
          },
          { root, threshold: 0.08 },
        );
        reveals.forEach((element) => {
          element.classList.remove('is-visible');
          observer?.observe(element);
        });
      }
      schedulePaint();
    };
    const resizeObserver = new ResizeObserver(schedulePaint);
    if (root.firstElementChild) resizeObserver.observe(root.firstElementChild);
    root.addEventListener('scroll', schedulePaint, { passive: true });
    window.addEventListener('resize', schedulePaint);
    reducedMotion.addEventListener('change', setupMotion);
    setupMotion();
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      root.classList.remove('landing-motion');
      root.style.scrollBehavior = previousScrollBehavior;
      resizeObserver.disconnect();
      root.removeEventListener('scroll', schedulePaint);
      window.removeEventListener('resize', schedulePaint);
      reducedMotion.removeEventListener('change', setupMotion);
    };
  }, [product, language]);

  const data = content.languages[language];
  const handleStart = () => openAuthPage('signin');
  const handleProductChange = (value: 'inspect' | 'ai') => {
    setProduct(value);
    setWorkflowStep(0);
    scrollContainerRef.current?.scrollTo({ top: 0 });
  };
  const RecordItems = data.recordItems.map(([title, description], index) => (
    <RecordItemContainer key={uuid4()}>
      <NumberContainer>
        <TextBlock text={`0${index + 1}`} design={createTextDesign(LANDING_COLOR.NUMBER)} />
      </NumberContainer>
      <Gap height='30px' />
      <TextBlock
        text={title}
        design={{ ...createTextDesign(), text: { custom: { fontSize: 22 }, color: LANDING_COLOR.INK } }}
      />
      <Gap height='14px' />
      <DescriptionContainer>
        <TextBlock text={description} design={createTextDesign(LANDING_COLOR.MUTED)} />
      </DescriptionContainer>
    </RecordItemContainer>
  ));
  const DamageItems = data.damages.map((damage, index) => (
    <DamageItemContainer key={uuid4()} $highlight={index === 0}>
      <TextBlock
        text={damage}
        design={{ text: { custom: { fontSize: 11 }, color: index === 0 ? LANDING_COLOR.PAPER : LANDING_COLOR.INK } }}
      />
    </DamageItemContainer>
  ));
  const CaptureStepItems = data.captureSteps.map((step, index) => (
    <CaptureStepItemContainer key={uuid4()}>
      <CaptureStepNumberContainer>
        <TextBlock text={`0${index + 1}`} design={{ text: { color: LANDING_COLOR.STEP_NUMBER } }} />
      </CaptureStepNumberContainer>
      <TextBlock text={step} design={{ text: { custom: { fontSize: 10 }, color: LANDING_COLOR.INK } }} />
    </CaptureStepItemContainer>
  ));
  const WorkflowTabItems = data.workflowTabs.map((label, index) => (
    <TextBlock
      key={uuid4()}
      text={label}
      design={{
        box: {
          width: 'fit-content',
          padding: '12px 20px',
          border: true,
          borderWidth: '1px',
          borderRadius: '25px',
          borderColor: workflowStep === index ? LANDING_COLOR.WORKFLOW_ACTIVE : LANDING_COLOR.WORKFLOW_BORDER,
          backgroundColor: workflowStep === index ? LANDING_COLOR.WORKFLOW_ACTIVE : 'transparent',
          event: { cursor: 'pointer' },
        },
        text: {
          custom: { fontSize: 12 },
          color: workflowStep === index ? LANDING_COLOR.PAPER : LANDING_COLOR.WORKFLOW_ACTIVE,
        },
      }}
      click={{ handler: () => setWorkflowStep(index) }}
    />
  ));
  const WorkflowRecordItems = data.exampleDamage.map((damage, index) => (
    <DatabaseRowContainer key={uuid4()}>
      <TextBlock
        text={`D-0${index + 1}`}
        design={{ box: { alignX: 'left' }, text: { custom: { fontSize: 10 }, color: LANDING_COLOR.INK, bold: true } }}
      />
      <TextBlock
        text={damage}
        design={{ box: { alignX: 'left' }, text: { custom: { fontSize: 11 }, color: LANDING_COLOR.INK } }}
      />
      <TextBlock
        text={['A-1', 'B-2', 'C-1'][index]}
        design={{ box: { alignX: 'left' }, text: { custom: { fontSize: 10 }, color: LANDING_COLOR.INK } }}
      />
    </DatabaseRowContainer>
  ));
  const createCadDrawing = (showMarks: boolean) => {
    const labels = [
      { text: 'A', x: 125, y: 275 },
      { text: 'B', x: 300, y: 275 },
      { text: 'C', x: 475, y: 275 },
      { text: '1', x: 30, y: 110 },
      { text: '2', x: 30, y: 205 },
      { text: 'GRIDGIO / DEMO DRAWING', x: 55, y: 25 },
    ];
    const CadLabelItems = labels.map((label) => (
      <CadLabelContainer key={uuid4()} $x={label.x} $y={label.y}>
        <TextBlock text={label.text} design={{ text: { custom: { fontSize: 11 }, color: LANDING_COLOR.CAD_LABEL } }} />
      </CadLabelContainer>
    ));
    const DamageLabelItems = [
      { text: 'D-01', x: 150, y: 140 },
      { text: 'D-02', x: 330, y: 213 },
      { text: 'D-03', x: 465, y: 90 },
    ].map((label) => (
      <CadLabelContainer key={uuid4()} $x={label.x} $y={label.y}>
        <TextBlock
          text={label.text}
          design={{ text: { custom: { fontSize: 12 }, color: LANDING_COLOR.DAMAGE_LABEL } }}
        />
      </CadLabelContainer>
    ));
    return (
      <CadVisualContainer>
        <svg viewBox='0 0 620 300'>
          <rect width='620' height='300' fill={LANDING_COLOR.CAD_BACKGROUND} />
          <g fill='none' stroke={LANDING_COLOR.CAD_LINE} strokeWidth='1'>
            <rect x='55' y='55' width='510' height='190' />
            <path d='M55 150H565M225 55V245M395 55V245' />
            <path d='M55 40H565M55 35V45M565 35V45' />
          </g>
          {showMarks && (
            <g fill='none' strokeWidth='3'>
              <path stroke={LANDING_COLOR.CRACK} d='M118 107l15 13-7 17 19 13-5 17' />
              <path stroke={LANDING_COLOR.LEAK} d='M310 195q-19 27 0 27q19 0 0-27z' />
              <rect x='465' y='100' width='46' height='33' stroke={LANDING_COLOR.SPALL} strokeDasharray='4 3' />
            </g>
          )}
        </svg>
        {CadLabelItems}
        {showMarks && DamageLabelItems}
      </CadVisualContainer>
    );
  };
  const WorkflowDrawing = createCadDrawing(workflowStep !== 1);
  const OutputDrawing = createCadDrawing(true);

  const ProductSelectItems = PRODUCTS.map((item) => (
    <SelectItemContainer key={uuid4()}>
      <TextBlock
        text={item.label}
        design={{
          box: { width: '100%', alignX: 'left', event: { cursor: 'pointer' } },
          text: { size: 'medium', color: LANDING_COLOR.INK, oneline: true },
        }}
        click={{
          handler: () => {
            handleProductChange(item.id);
            if (productSelectRef.current) productSelectRef.current.open = false;
          },
        }}
      />
    </SelectItemContainer>
  ));
  const LanguageSelectItems = LANGUAGES.map((item) => (
    <SelectItemContainer key={uuid4()}>
      <TextBlock
        text={item.label}
        design={{
          box: { width: '100%', alignX: 'left', event: { cursor: 'pointer' } },
          text: { size: 'medium', color: LANDING_COLOR.INK, oneline: true },
        }}
        click={{
          handler: () => {
            setLanguage(item.id);
            setWorkflowStep(0);
            scrollContainerRef.current?.scrollTo({ top: 0 });
            if (languageSelectRef.current) languageSelectRef.current.open = false;
          },
        }}
      />
    </SelectItemContainer>
  ));

  const ReportFieldItems = data.reportFields.map((field) => (
    <DraftFieldContainer key={uuid4()}>
      <TextBlock
        text={field}
        design={{ box: { alignX: 'left' }, text: { custom: { fontSize: 12 }, color: LANDING_COLOR.INK } }}
      />
      <Gap height='12px' />
      <DraftLine />
      <DraftLine $short />
    </DraftFieldContainer>
  ));

  return (
    <>
      <Wrapper>
        <Helmet title={`${product === 'inspect' ? 'GridGIO-Inspect' : 'GridGIO-AI'} | GridGIO`} />
        <Header>
          <BrandContainer>
            <TextBlock
              text='GridGIO'
              design={{ text: { size: 'huge', color: LANDING_COLOR.INK, bold: true } }}
              click={{ handler: () => handleProductChange('inspect') }}
            />
          </BrandContainer>
          <HeaderControlContainer>
            <SelectContainer
              ref={productSelectRef}
              onToggle={(event) => {
                if (event.currentTarget.open && languageSelectRef.current) languageSelectRef.current.open = false;
              }}>
              <SelectTriggerContainer>
                <IconTextBlock
                  text={product === 'inspect' ? 'GridGIO-Inspect' : 'GridGIO-AI'}
                  icon='down'
                  arrangement='text-icon'
                  design={{
                    box: { gap: size.MARGIN_1 },
                    text: { size: 'medium', color: LANDING_COLOR.INK },
                    icon: { size: 'small', color: LANDING_COLOR.INK },
                  }}
                />
              </SelectTriggerContainer>
              <SelectItemsContainer>{ProductSelectItems}</SelectItemsContainer>
            </SelectContainer>
            <SelectContainer
              ref={languageSelectRef}
              onToggle={(event) => {
                if (event.currentTarget.open && productSelectRef.current) productSelectRef.current.open = false;
              }}>
              <SelectTriggerContainer>
                <IconTextBlock
                  text={LANGUAGES.find((item) => item.id === language)!.label}
                  icon='down'
                  arrangement='text-icon'
                  design={{
                    box: { gap: size.MARGIN_1 },
                    text: { size: 'medium', color: LANDING_COLOR.INK },
                    icon: { size: 'small', color: LANDING_COLOR.INK },
                  }}
                />
              </SelectTriggerContainer>
              <SelectItemsContainer $minWidth='120px'>{LanguageSelectItems}</SelectItemsContainer>
            </SelectContainer>
            <TextBlock
              text={data.contact}
              design={{
                box: {
                  width: '110px',
                  height: size.DESIGN_SYSTEM_BUTTON_MEDIUM_HEIGHT,
                  backgroundColor: 'transparent',
                  border: true,
                  borderWidth: size.DESIGN_SYSTEM_BUTTON_BORDER_WIDTH,
                  borderColor: LANDING_COLOR.MUTED,
                  borderRadius: size.DESIGN_SYSTEM_BUTTON_BORDER_RADIUS,
                  event: { cursor: 'pointer' },
                },
                text: { size: 'medium', color: LANDING_COLOR.MUTED },
              }}
              click={{ handler: () => contactRef.current?.scrollIntoView({ block: 'start' }) }}
            />
            <TextBlock
              text={language === 'ko' ? '시작하기' : language === 'ja' ? 'はじめる' : 'Get started'}
              design={{
                box: {
                  width: '110px',
                  height: size.DESIGN_SYSTEM_BUTTON_MEDIUM_HEIGHT,
                  backgroundColor: LANDING_COLOR.INK,
                  borderRadius: size.DESIGN_SYSTEM_BUTTON_BORDER_RADIUS,
                  event: { cursor: 'pointer' },
                },
                text: { size: 'medium', color: LANDING_COLOR.PAPER },
              }}
              click={{ handler: handleStart }}
            />
          </HeaderControlContainer>
        </Header>
        <HeroContainer>
          <ContentContainer>
            <EyebrowContainer>
              <StatusDot />
              <TextBlock
                text={product === 'inspect' ? 'GRIDGIO-INSPECT / FIELD WORKFLOW' : 'GRIDGIO-AI / INTELLIGENT WORKFLOW'}
                design={{
                  box: { alignX: 'left' },
                  text: { custom: { fontSize: 10 }, color: LANDING_COLOR.INK, bold: true },
                }}
              />
            </EyebrowContainer>
            <HeroHeadingContainer>
              <TextBlock text={(product === 'inspect' ? data.hero : data.aiHero)[0]} design={createTextDesign()} />
              <TextBlock
                text={(product === 'inspect' ? data.hero : data.aiHero)[1]}
                design={createTextDesign(LANDING_COLOR.HERO_ACCENT)}
              />
            </HeroHeadingContainer>
            <Gap height='30px' />
            <HeroBottomContainer>
              <DescriptionContainer>
                <TextBlock
                  text={product === 'inspect' ? data.intro : data.aiIntro}
                  design={createTextDesign(LANDING_COLOR.MUTED)}
                />
              </DescriptionContainer>
              <IconTextBlock
                icon='down'
                text={data.explore}
                arrangement='text-icon'
                design={{
                  box: { gap: '22px', event: { cursor: 'pointer' } },
                  text: { size: 'small', color: LANDING_COLOR.INK },
                  icon: { size: 'small', color: LANDING_COLOR.INK },
                }}
                click={{ handler: () => featureRef.current?.scrollIntoView({ block: 'start' }) }}
              />
            </HeroBottomContainer>
            <Gap height='55px' />
            <ImageContainer ref={heroImageRef}>
              <ImageBlock
                src={product === 'inspect' ? tabletImage : reportImage}
                alt={product === 'inspect' ? 'GridGIO-Inspect' : 'GridGIO-AI'}
                design={IMAGE_DESIGN}
              />
              <ImageLabelContainer>
                <TextBlock
                  text={product === 'inspect' ? 'FIELD WORK, CONNECTED.' : 'GRIDGIO-AI'}
                  design={{ text: { size: 'tiny', color: LANDING_COLOR.PAPER } }}
                />
              </ImageLabelContainer>
            </ImageContainer>
            <Gap height='20px' />
            <MetaContainer>
              <TextBlock text={`${data.scroll} ↓`} design={{ text: { size: 'tiny', color: LANDING_COLOR.INK } }} />
              <TextBlock text='GRIDGIO / 01' design={{ text: { size: 'tiny', color: LANDING_COLOR.INK } }} />
            </MetaContainer>
          </ContentContainer>
        </HeroContainer>
        {product === 'inspect' && (
          <>
            <RecordSection ref={featureRef}>
              <ContentContainer>
                <EyebrowContainer>
                  <TextBlock text='01 / RECORD' design={{ text: { size: 'tiny', color: LANDING_COLOR.INK } }} />
                </EyebrowContainer>
                <HeadingContainer>
                  <TextBlock text={data.record[0]} design={createTextDesign()} />
                </HeadingContainer>
                <Gap height='26px' />
                <DescriptionContainer>
                  <TextBlock text={data.record[1]} design={createTextDesign(LANDING_COLOR.MUTED)} />
                </DescriptionContainer>
                <Gap height='60px' />
                <RecordItemsContainer>{RecordItems}</RecordItemsContainer>
                <Gap height='40px' />
                <TagItemsContainer>{DamageItems}</TagItemsContainer>
              </ContentContainer>
            </RecordSection>
            <CaptureSection ref={captureSectionRef}>
              <CaptureStageContainer>
                <ContentContainer>
                  <CaptureCopyContainer>
                    <EyebrowContainer>
                      <TextBlock text='02 / CAPTURE' design={{ text: { size: 'tiny', color: LANDING_COLOR.INK } }} />
                    </EyebrowContainer>
                    <HeadingContainer>
                      <TextBlock text={data.capture[0]} design={createTextDesign()} />
                    </HeadingContainer>
                    <CaptureDescriptionContainer>
                      <TextBlock text={data.capture[1]} design={createTextDesign(LANDING_COLOR.MUTED)} />
                    </CaptureDescriptionContainer>
                    <Gap height='25px' />
                    <StepItemsContainer>{CaptureStepItems}</StepItemsContainer>
                  </CaptureCopyContainer>
                </ContentContainer>
                <CaptureImageContainer ref={captureImageRef}>
                  <ImageContainer>
                    <ImageBlock src={captureImage} alt={data.capture[0]} design={IMAGE_DESIGN} />
                  </ImageContainer>
                </CaptureImageContainer>
              </CaptureStageContainer>
            </CaptureSection>
            <CloudSection $background={LANDING_COLOR.INK}>
              <ContentContainer>
                <SplitContainer>
                  <div>
                    <EyebrowContainer>
                      <TextBlock text='03 / CONNECT' design={{ text: { size: 'tiny', color: LANDING_COLOR.PAPER } }} />
                    </EyebrowContainer>
                    <HeadingContainer>
                      <TextBlock text={data.cloud[0]} design={createTextDesign(LANDING_COLOR.PAPER)} />
                    </HeadingContainer>
                  </div>
                  <div>
                    <DescriptionContainer>
                      <TextBlock text={data.cloud[1]} design={createTextDesign(LANDING_COLOR.DARK_DESCRIPTION)} />
                    </DescriptionContainer>
                    <Gap height='24px' />
                    <TextBlock
                      text={data.paidCloud}
                      design={{
                        box: {
                          width: 'fit-content',
                          padding: '9px 13px',
                          border: true,
                          borderWidth: '1px',
                          borderColor: LANDING_COLOR.CLOUD_LABEL_BORDER,
                          borderRadius: '24px',
                          backgroundColor: 'transparent',
                        },
                        text: { custom: { fontSize: 9 }, color: LANDING_COLOR.PAPER },
                      }}
                    />
                  </div>
                </SplitContainer>
                <Gap height='60px' />
                <CloudImageContainer>
                  <ImageContainer>
                    <ImageBlock src={cloudImage} alt={data.cloud[0]} design={IMAGE_DESIGN} />
                  </ImageContainer>
                  <CloudWordContainer>
                    <TextBlock text='Connected.' design={createTextDesign(LANDING_COLOR.CLOUD_ACCENT)} />
                  </CloudWordContainer>
                </CloudImageContainer>
              </ContentContainer>
            </CloudSection>
            <Section>
              <ContentContainer>
                <SplitContainer>
                  <div>
                    <EyebrowContainer>
                      <TextBlock
                        text='04 / YOUR NEXT STEP'
                        design={{ text: { size: 'tiny', color: LANDING_COLOR.INK } }}
                      />
                    </EyebrowContainer>
                    <HeadingContainer>
                      <TextBlock text={data.migration[0]} design={createTextDesign()} />
                    </HeadingContainer>
                    <Gap height='24px' />
                    <DescriptionContainer>
                      <TextBlock text={data.migration[1]} design={createTextDesign(LANDING_COLOR.MUTED)} />
                    </DescriptionContainer>
                    <Gap height='16px' />
                    <TextBlock
                      text={data.migration[2]}
                      design={{ text: { size: 'small', color: LANDING_COLOR.MUTED } }}
                    />
                    <Gap height='32px' />
                    <BoxBlock
                      design={{ box: createLandingButtonDesign().box }}
                      onClick={() => handleProductChange('ai')}>
                      <TextBlock text={data.aiLink} design={{ text: createLandingButtonDesign().text }} />
                      <TextBlock text='↗' design={{ text: createLandingButtonDesign().text }} />
                    </BoxBlock>
                  </div>
                  <MigrationVisualContainer>
                    <DrawingSheetContainer>
                      <TextBlock text='CAD' design={{ text: { size: 'huge', color: LANDING_COLOR.INK } }} />
                      <DrawingGrid />
                      <Gap height='30px' />
                      <TextBlock text='DRAWING' design={{ text: { size: 'tiny', color: LANDING_COLOR.MUTED } }} />
                    </DrawingSheetContainer>
                    <ConvertArrowContainer>
                      <TextBlock
                        text='↗'
                        design={{ text: { custom: { fontSize: 60 }, color: LANDING_COLOR.DRAWING_LINE } }}
                      />
                    </ConvertArrowContainer>
                    <DatabaseSheetContainer>
                      <TextBlock text='DB' design={{ text: { size: 'huge', color: LANDING_COLOR.PAPER } }} />
                      <DataLine>
                        <TextBlock text='01' design={{ text: { size: 'tiny', color: LANDING_COLOR.PAPER } }} />
                      </DataLine>
                      <DataLine>
                        <TextBlock text='02' design={{ text: { size: 'tiny', color: LANDING_COLOR.PAPER } }} />
                      </DataLine>
                      <DataLine>
                        <TextBlock text='03' design={{ text: { size: 'tiny', color: LANDING_COLOR.PAPER } }} />
                      </DataLine>
                      <Gap height='30px' />
                      <TextBlock
                        text='CONNECTED DATA'
                        design={{ text: { size: 'tiny', color: LANDING_COLOR.PAPER } }}
                      />
                    </DatabaseSheetContainer>
                    <MigrationLabelContainer>
                      <TextBlock
                        text='POWERED BY GRIDGIO-AI'
                        design={{
                          box: { alignX: 'left' },
                          text: { custom: { fontSize: 8 }, color: LANDING_COLOR.INK },
                        }}
                      />
                    </MigrationLabelContainer>
                  </MigrationVisualContainer>
                </SplitContainer>
              </ContentContainer>
            </Section>
          </>
        )}
        {product === 'ai' && (
          <>
            <Section ref={featureRef}>
              <ContentContainer>
                <EyebrowContainer>
                  <TextBlock
                    text='01 / CAD TO DATABASE'
                    design={{ text: { size: 'tiny', color: LANDING_COLOR.INK } }}
                  />
                </EyebrowContainer>
                <HeadingContainer>
                  <TextBlock text={data.aiSections[0][0]} design={createTextDesign()} />
                </HeadingContainer>
                <Gap height='26px' />
                <DescriptionContainer>
                  <TextBlock text={data.aiSections[0][1]} design={createTextDesign(LANDING_COLOR.MUTED)} />
                </DescriptionContainer>
                <WorkflowContainer>
                  <TextBlock
                    text={data.workflowTitle}
                    design={{ box: { alignX: 'left' }, text: { custom: { fontSize: 21 }, color: LANDING_COLOR.INK } }}
                  />
                  <WorkflowControlContainer>{WorkflowTabItems}</WorkflowControlContainer>
                  <WorkflowGridContainer>
                    <CadContainer>
                      {WorkflowDrawing}
                      <Gap height='14px' />
                      <TextBlock
                        text={workflowStep === 1 ? data.baseLabel : data.drawLabel}
                        design={{
                          box: { alignX: 'left' },
                          text: { custom: { fontSize: 11 }, color: LANDING_COLOR.INK },
                        }}
                      />
                    </CadContainer>
                    {workflowStep !== 0 && (
                      <DatabaseContainer>
                        <TextBlock
                          text={data.dbLabel}
                          design={{
                            box: { alignX: 'left' },
                            text: { custom: { fontSize: 16 }, color: LANDING_COLOR.INK },
                          }}
                        />
                        <Gap height='20px' />
                        <DatabaseRowContainer>
                          <TextBlock
                            text='ID'
                            design={{
                              box: { alignX: 'left' },
                              text: { custom: { fontSize: 8 }, color: LANDING_COLOR.DATABASE_HEADER },
                            }}
                          />
                          <TextBlock
                            text={data.typeLabel}
                            design={{
                              box: { alignX: 'left' },
                              text: { custom: { fontSize: 8 }, color: LANDING_COLOR.DATABASE_HEADER },
                            }}
                          />
                          <TextBlock
                            text={data.positionLabel}
                            design={{
                              box: { alignX: 'left' },
                              text: { custom: { fontSize: 8 }, color: LANDING_COLOR.DATABASE_HEADER },
                            }}
                          />
                        </DatabaseRowContainer>
                        {WorkflowRecordItems}
                      </DatabaseContainer>
                    )}
                  </WorkflowGridContainer>
                  <Gap height='20px' />
                  <DescriptionContainer>
                    <TextBlock
                      text={data.workflowDesc[workflowStep]}
                      design={{
                        ...createTextDesign(LANDING_COLOR.MUTED),
                        text: { custom: { fontSize: 12 }, color: LANDING_COLOR.MUTED },
                      }}
                    />
                  </DescriptionContainer>
                  <Gap height='20px' />
                  <TextBlock
                    text={data.exampleNote}
                    design={{
                      box: { alignX: 'left' },
                      text: { custom: { fontSize: 10 }, color: LANDING_COLOR.MUTED },
                    }}
                  />
                </WorkflowContainer>
              </ContentContainer>
            </Section>
            <Section $background={LANDING_COLOR.AI_BACKGROUND}>
              <ContentContainer>
                <AiSplitContainer>
                  <div>
                    <EyebrowContainer>
                      <TextBlock
                        text='02 / DATABASE TO CAD'
                        design={{ text: { size: 'tiny', color: LANDING_COLOR.PAPER } }}
                      />
                    </EyebrowContainer>
                    <HeadingContainer>
                      <TextBlock text={data.aiSections[1][0]} design={createTextDesign(LANDING_COLOR.PAPER)} />
                    </HeadingContainer>
                    <Gap height='25px' />
                    <DescriptionContainer>
                      <TextBlock text={data.aiSections[1][1]} design={createTextDesign(LANDING_COLOR.AI_DESCRIPTION)} />
                    </DescriptionContainer>
                    <Gap height='24px' />
                    <TextBlock
                      text={data.aiBadge}
                      design={{
                        box: {
                          width: 'fit-content',
                          padding: '9px 13px',
                          border: true,
                          borderWidth: '1px',
                          borderColor: LANDING_COLOR.CLOUD_LABEL_BORDER,
                          borderRadius: '24px',
                        },
                        text: { custom: { fontSize: 9 }, color: LANDING_COLOR.PAPER },
                      }}
                    />
                  </div>
                  <div>
                    {OutputDrawing}
                    <Gap height='16px' />
                    <TextBlock
                      text={data.outputCaption}
                      design={{
                        box: { alignX: 'left' },
                        text: { custom: { fontSize: 10 }, color: LANDING_COLOR.AI_DESCRIPTION },
                      }}
                    />
                  </div>
                </AiSplitContainer>
              </ContentContainer>
            </Section>
            <Section>
              <ContentContainer>
                <AiSplitContainer>
                  <div>
                    <EyebrowContainer>
                      <TextBlock
                        text='03 / REPORT DRAFT'
                        design={{ text: { size: 'tiny', color: LANDING_COLOR.INK } }}
                      />
                    </EyebrowContainer>
                    <HeadingContainer>
                      <TextBlock text={data.aiSections[2][0]} design={createTextDesign()} />
                    </HeadingContainer>
                    <Gap height='26px' />
                    <DescriptionContainer>
                      <TextBlock text={data.aiSections[2][1]} design={createTextDesign(LANDING_COLOR.MUTED)} />
                    </DescriptionContainer>
                    <Gap height='25px' />
                    <DescriptionContainer>
                      <TextBlock text={data.reportQA} design={createTextDesign(LANDING_COLOR.MUTED)} />
                    </DescriptionContainer>
                  </div>
                  <DraftContainer>
                    <TextBlock
                      text={data.draftBadge}
                      design={{
                        box: {
                          width: 'fit-content',
                          padding: '8px 12px',
                          backgroundColor: LANDING_COLOR.DRAFT_LABEL_BACKGROUND,
                        },
                        text: { custom: { fontSize: 10 }, color: LANDING_COLOR.DRAFT_LABEL_TEXT },
                      }}
                    />
                    <Gap height='24px' />
                    <DraftHeadingContainer>
                      <TextBlock
                        text={data.reportLabel}
                        design={{
                          box: { alignX: 'left' },
                          text: { custom: { fontSize: 26 }, color: LANDING_COLOR.INK },
                        }}
                      />
                    </DraftHeadingContainer>
                    {ReportFieldItems}
                    <Gap height='30px' />
                    <TextBlock
                      text='GRIDGIO-AI / CONCEPT'
                      design={{
                        box: { alignX: 'left' },
                        text: { custom: { fontSize: 8 }, color: LANDING_COLOR.DATABASE_HEADER },
                      }}
                    />
                  </DraftContainer>
                </AiSplitContainer>
                <DetectionContainer>
                  <TextBlock
                    text='GRIDGIO-AI / ANALYSIS & EVIDENCE'
                    design={{ box: { alignX: 'left' }, text: { custom: { fontSize: 9 }, color: LANDING_COLOR.INK } }}
                  />
                  <Gap height='18px' />
                  <TextBlock
                    text={data.detectTitle}
                    design={{ box: { alignX: 'left' }, text: { custom: { fontSize: 22 }, color: LANDING_COLOR.INK } }}
                  />
                  <Gap height='14px' />
                  <DescriptionContainer>
                    <TextBlock text={data.detectText} design={createTextDesign(LANDING_COLOR.MUTED)} />
                  </DescriptionContainer>
                </DetectionContainer>
                <BackLinkContainer>
                  <BoxBlock
                    design={{ box: { gap: '22px', event: { cursor: 'pointer' } } }}
                    onClick={() => handleProductChange('inspect')}>
                    <TextBlock
                      text={data.back}
                      design={{ text: { custom: { fontSize: 12 }, color: LANDING_COLOR.INK } }}
                    />
                    <TextBlock text='↗' design={{ text: { custom: { fontSize: 12 }, color: LANDING_COLOR.INK } }} />
                  </BoxBlock>
                </BackLinkContainer>
              </ContentContainer>
            </Section>
          </>
        )}
        <PlansSection $background={LANDING_COLOR.PLAN_BACKGROUND}>
          <ContentContainer>
            <EyebrowContainer>
              <TextBlock
                text={product === 'inspect' ? '05 / PLANS' : '04 / PLANS'}
                design={{ text: { size: 'tiny', color: LANDING_COLOR.INK } }}
              />
            </EyebrowContainer>
            <HeadingContainer>
              <TextBlock text={data.plans[0]} design={createTextDesign()} />
              <TextBlock text={data.plans[1]} design={createTextDesign(LANDING_COLOR.PLAN_ACCENT)} />
            </HeadingContainer>
            <Gap height='55px' />
            <PlanItemsContainer>
              <PlanItemContainer>
                <TextBlock
                  text={data.free}
                  design={{
                    box: {
                      width: 'fit-content',
                      padding: '7px 12px',
                      borderRadius: '20px',
                      backgroundColor: LANDING_COLOR.FREE_LABEL,
                    },
                    text: { custom: { fontSize: 10 }, color: LANDING_COLOR.INK },
                  }}
                />
                <Gap height='24px' />
                <TextBlock
                  text={data.freeTitle}
                  design={{
                    box: { width: '100%', alignX: 'left' },
                    text: { custom: { fontSize: 21 }, color: LANDING_COLOR.INK },
                  }}
                />
                <Gap height='14px' />
                <DescriptionContainer>
                  <TextBlock text={data.freeText} design={createTextDesign(LANDING_COLOR.MUTED)} />
                </DescriptionContainer>
                <PlanMarkContainer>
                  <TextBlock text='↗' design={{ text: { custom: { fontSize: 25 }, color: LANDING_COLOR.INK } }} />
                </PlanMarkContainer>
              </PlanItemContainer>
              <PlanItemContainer>
                <TextBlock
                  text={data.paid}
                  design={{
                    box: {
                      width: 'fit-content',
                      padding: '7px 12px',
                      borderRadius: '20px',
                      backgroundColor: LANDING_COLOR.INK,
                    },
                    text: { custom: { fontSize: 10 }, color: LANDING_COLOR.PAPER },
                  }}
                />
                <Gap height='24px' />
                <TextBlock
                  text={data.paidTitle}
                  design={{
                    box: { width: '100%', alignX: 'left' },
                    text: { custom: { fontSize: 21 }, color: LANDING_COLOR.INK },
                  }}
                />
                <Gap height='14px' />
                <DescriptionContainer>
                  <TextBlock text={data.paidText} design={createTextDesign(LANDING_COLOR.MUTED)} />
                </DescriptionContainer>
                <Gap height='15px' />
                <IconTextBlock
                  text={data.contact}
                  icon='forward'
                  arrangement='text-icon'
                  design={{
                    box: { width: 'fit-content', alignX: 'left', gap: '10px', event: { cursor: 'pointer' } },
                    text: { custom: { fontSize: 11 }, color: LANDING_COLOR.INK },
                    icon: { size: 'tiny', color: LANDING_COLOR.INK },
                  }}
                  click={{ handler: () => contactRef.current?.scrollIntoView({ block: 'start' }) }}
                />
              </PlanItemContainer>
            </PlanItemsContainer>
          </ContentContainer>
        </PlansSection>
        <ContactSection ref={contactRef} $background={LANDING_COLOR.FOOTER_BACKGROUND}>
          <ContentContainer>
            <EyebrowContainer>
              <TextBlock text='CONTACT US' design={{ text: { size: 'tiny', color: LANDING_COLOR.FOOTER_LABEL } }} />
            </EyebrowContainer>
            <HeadingContainer>
              <TextBlock text={data.contactTitle} design={createTextDesign(LANDING_COLOR.PAPER)} />
            </HeadingContainer>
            <Gap height='35px' />
            <SplitContainer>
              <DescriptionContainer>
                <TextBlock text={data.contactText} design={createTextDesign(LANDING_COLOR.FOOTER_DESCRIPTION)} />
              </DescriptionContainer>
              <TextBlock
                text={data.pending}
                design={{ text: { size: 'small', color: LANDING_COLOR.FOOTER_DESCRIPTION } }}
              />
            </SplitContainer>
            <FooterContainer>
              <BrandContainer>
                <TextBlock
                  text='GridGIO'
                  design={{ text: { size: 'big', color: LANDING_COLOR.PAPER, bold: true } }}
                  click={{ handler: () => handleProductChange('inspect') }}
                />
              </BrandContainer>
              <TextBlock
                text='SAFETY INSPECTION, CONNECTED.'
                design={{ text: { size: 'tiny', color: LANDING_COLOR.PAPER } }}
              />
              <IconTextBlock
                icon='up'
                text=''
                design={{
                  box: { event: { cursor: 'pointer' } },
                  icon: { size: 'large', color: LANDING_COLOR.PAPER },
                }}
                click={{ handler: () => scrollContainerRef.current?.scrollTo({ top: 0 }) }}
              />
            </FooterContainer>
            <Gap height='28px' />
            <DisclaimerContainer>
              <TextBlock
                text={data.demo}
                design={{
                  box: { width: '100%', alignX: 'left' },
                  text: { custom: { fontSize: 9 }, color: LANDING_COLOR.FOOTER_NOTE },
                }}
              />
            </DisclaimerContainer>
          </ContentContainer>
        </ContactSection>
      </Wrapper>
      <ProgressContainer ref={progressRef} />
    </>
  );
};

export default Landing;
